import { useState } from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';

interface SSOLoginFormProps {
  onBack?: () => void;
  onSuccess?: (user: any, token: string) => void;
  onError?: (error: string) => void;
}

export const SSOLoginForm: React.FC<SSOLoginFormProps> = ({
  onBack,
  onSuccess,
  onError,
}) => {
  const {
    isLoading,
    error,
    performSSOLogin,
    goToLogin,
    clearAuthError,
    validatePassword,
  } = useAuth();

  const [formData, setFormData] = useState({
    userId: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (name: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    // Validate userId (can be email or username)
    if (!formData.userId) {
      errors.userId = 'User ID is required';
    } else if (!/^[a-zA-Z0-9]*$/.test(formData.userId)) {
      errors.userId = 'User ID can only contain letters and numbers';
    }
    
    // Validate password
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      errors.password = passwordError;
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const result = await performSSOLogin({
        userId: formData.userId,
        password: formData.password,
      });

      if (result.type === 'auth/performSSOLogin/fulfilled') {
        const payload = result.payload as any;
        const user = payload.user || payload.data;
        const token = payload.data?.token || payload.token || payload.access_token;
        console.log('React SSOForm: Extracted token', { 
          tokenExists: !!token, 
          tokenPreview: token?.substring(0, 20) + '...',
          payload: payload 
        });
        onSuccess?.(user, token);
      } else {
        const errorMessage = result.payload as string || 'SSO login failed';
        onError?.(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      onError?.(errorMessage);
    }
  };

  const handleBack = () => {
    goToLogin();
    onBack?.();
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Sign In
      </button>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          LDAP Sign In
        </h1>
        <p className="text-gray-600">
          Sign in using your organization credentials
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" className="mb-6" onClose={clearAuthError}>
          {error}
        </Alert>
      )}

      {/* SSO Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField
          label="User ID"
          type="text"
          name="userId"
          value={formData.userId}
          onChange={handleInputChange('userId')}
          error={formErrors.userId}
          required
          disabled={isLoading}
          placeholder="Enter your User ID"
        />
        
        <FormField
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleInputChange('password')}
          error={formErrors.password}
          required
          disabled={isLoading}
          placeholder="Enter your password"
        />
        
        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          disabled={isLoading || !formData.userId || !formData.password}
          size="lg"
          variant="gradient"
        >
          Sign In with LDAP
        </Button>
      </form>

      {/* Divider */}
      <div className="mt-8 mb-8">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or</span>
          </div>
        </div>
      </div>

      {/* Regular Login Option */}
      <Button
        onClick={handleBack}
        variant="outline"
        fullWidth
        disabled={isLoading}
      >
        Sign in with regular account
      </Button>

      {/* Help Text */}
      <div className="mt-8">
        <Alert variant="info">
          <div className="text-sm">
            <div className="font-medium mb-2">Need help with LDAP login?</div>
            <ul className="space-y-1 text-gray-600">
              <li>• Contact your IT administrator for credentials</li>
              <li>• Use your organization's User ID (not email)</li>
              <li>• Make sure LDAP access is enabled for your account</li>
            </ul>
          </div>
        </Alert>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>
          Having trouble? Contact{' '}
          <a href="#" className="text-blue-600 hover:text-blue-500">
            support
          </a>
        </p>
      </div>
    </div>
  );
};